import { Button } from "@mui/material";
import useAuth from "../../../hooks/useAuth";
import { useMsal } from "@azure/msal-react";
import FileUploadModal from "../FileUploadModal/FileUploadModal";
import { GridRowModel, GridValidRowModel } from "@mui/x-data-grid";
import { useState } from "react";
import axios from "axios";
import { apiVariable } from "../../../api/apiVariable";
import { useQueryClient } from "@tanstack/react-query";
import NotesModal from "../NotesModal/NotesModal";

const handleApproveOrDecline = async (
  id: any,
  username: string,
  name: string | undefined,
  notes: any,
  token: string | null | undefined,
  isApproved: boolean
) => {
  try {
    await axios.put(
      `${apiVariable}/approveMoneyRequest`,
      {
        MoneyRequestId: id,
        DateApproved: new Date(),
        UsernameApprovedBy: username,
        NameApprovedBy: name,
        ApprovalNotes: notes,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          isApproved: isApproved,
        },
      }
    );
  } catch (error) {
    console.error("Error:", error);
  }
};

const MoneyRequestActionButtons = ({ params }: { params: any }) => {
  const { token, role } = useAuth();
  const { accounts } = useMsal();
  const [currentRow, setCurrentRow] = useState<GridRowModel | null>(null);
  const [isInvoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [isReceiptModalOpen, setReceiptModalOpen] = useState(false);
  const [isNotesModalOpen, setNotesModalOpen] = useState(false);
  const [isApproved, setIsApproved] = useState(false);
  const userName = accounts[0].username;
  const name = accounts[0].name;
  const queryClient = useQueryClient();

  const openInvoiceModal = (row: GridValidRowModel) => {
    setCurrentRow(row);
    setInvoiceModalOpen(true);
  };

  const openReceiptModal = (row: GridValidRowModel) => {
    if (row) {
      setCurrentRow(row);
      setReceiptModalOpen(true);
    }
  };

  const handleInvoiceClose = () => {
    setInvoiceModalOpen(false);
    if (currentRow) {
      if (
        currentRow.filesAvailability !== "receipt" &&
        currentRow.filesAvailability !== "both"
      ) {
        openReceiptModal(currentRow);
      }
    }
  };

  const openNotesModal = (approved: boolean) => {
    setIsApproved(approved);
    setNotesModalOpen(true);
  };

  const handleNotesSubmit = async (notes: any, isApproved: boolean) => {
    if (currentRow) {
      await handleApproveOrDecline(
        currentRow.id,
        userName,
        name,
        notes,
        token,
        isApproved
      );
      await queryClient.invalidateQueries({ queryKey: ["moneyRequests"] });
      await queryClient.invalidateQueries({
        queryKey: ["needingApprovalRequests"],
      });
      await queryClient.invalidateQueries({ queryKey: ["allRequests"] });
    }
  };

  return (
    <div>
      {params.row.requiresApproval && role?.includes("Accounting") && (
        <>
          <Button
            variant="contained"
            color="success"
            size="small"
            onClick={() => {
              setCurrentRow(params.row);
              openNotesModal(true);
            }}
            sx={{ marginRight: 1 }}
          >
            Approve
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={() => {
              setCurrentRow(params.row);
              openNotesModal(false);
            }}
            sx={{ marginRight: 1 }}
          >
            Decline
          </Button>
        </>
      )}
      {(params.row.status === "approved" ||
        params.row.status === "completedWithoutApproval") &&
        params.row.filesAvailability !== "both" && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => {
              if (params.row.filesAvailability === "invoice") {
                openReceiptModal(params.row);
              } else if (params.row.filesAvailability === "receipt") {
                openInvoiceModal(params.row);
              } else {
                openInvoiceModal(params.row);
              }
            }}
          >
            {params.row.filesAvailability === "invoice"
              ? "Upload Receipt"
              : params.row.filesAvailability === "receipt"
              ? "Upload Invoice"
              : "Upload Invoice/Receipt"}
          </Button>
        )}
      <FileUploadModal
        open={isInvoiceModalOpen}
        onClose={() => handleInvoiceClose()}
        row={currentRow}
        type="moneyRequestInvoice"
      />

      <FileUploadModal
        open={isReceiptModalOpen}
        onClose={() => setReceiptModalOpen(false)}
        row={currentRow}
        type="moneyRequestReceipt"
      />
      <NotesModal
        open={isNotesModalOpen}
        onClose={() => setNotesModalOpen(false)}
        onSubmit={handleNotesSubmit}
        isApproved={isApproved}
      />
    </div>
  );
};

export default MoneyRequestActionButtons;
